import { useEffect, useState } from "react";
import { useLocation } from "wouter";
import { useAppStore } from "../../store/app";
import { useT } from "../../lib/i18n/useT";
import { readAllVacancies, type Vacancy } from "../../lib/api/vacancies";
import { readAllWorkflows, createWorkflow, type Workflow } from "../../lib/api/workflows";
import VacancyDetailModal from "./VacancyDetailModal";

function getDaysRemaining(deadlineStr: string): number {
  const now = new Date();
  const deadlineDate = new Date(deadlineStr);
  const diffTime = deadlineDate.getTime() - now.getTime();
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
}

export default function VacancyLibrary() {
  const t = useT();
  const repoPath = useAppStore((s) => s.repoPath);
  const user = useAppStore((s) => s.user);
  const language = useAppStore((s) => s.language);
  const [, navigate] = useLocation();

  const activeCandidateFolder = useAppStore((s) => s.activeCandidateFolder);

  const [vacancies, setVacancies] = useState<Vacancy[]>([]);
  const [workflows, setWorkflows] = useState<Workflow[]>([]);
  const [loading, setLoading] = useState(true);
  const [startingWfId, setStartingWfId] = useState<string | null>(null);
  const [selectedVacancy, setSelectedVacancy] = useState<Vacancy | null>(null);
  const [reloadNonce, setReloadNonce] = useState(0);

  // Filters state
  const [search, setSearch] = useState("");
  const [remoteFilter, setRemoteFilter] = useState<string>("all");
  const [showExpired, setShowExpired] = useState(false);

  useEffect(() => {
    if (!repoPath || !activeCandidateFolder) return;
    
    let active = true;
    (async () => {
      setLoading(true);
      try {
        const [allVacancies, allWorkflows] = await Promise.all([
          readAllVacancies(repoPath),
          readAllWorkflows(repoPath, activeCandidateFolder),
        ]);
        if (active) {
          setVacancies(allVacancies);
          setWorkflows(allWorkflows);
        }
      } catch (err) {
        console.error("Error loading library data", err);
      } finally {
        if (active) setLoading(false);
      }
    })();

    return () => {
      active = false;
    };
  }, [repoPath, activeCandidateFolder, reloadNonce]);

  async function handleStartWorkflow(vacancyId: string) {
    if (!repoPath || !activeCandidateFolder || !user?.oid) return;
    
    setStartingWfId(vacancyId);
    try {
      // Create new workflow on filesystem (spec §9)
      const newWf = await createWorkflow(repoPath, activeCandidateFolder, user.oid, vacancyId);
      
      // Update local workflows list
      setWorkflows((prev) => [...prev, newWf]);
      setStartingWfId(null);
      
      navigate(`/workflows/${newWf.id}`);
    } catch (err) {
      console.error("Failed to start workflow", err);
      setStartingWfId(null);
    }
  }

  // Filter out expired vacancies (deadline < today) if showExpired is false
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const visibleVacancies = vacancies.filter((v) => {
    if (showExpired) return true;

    const earliestDeadline = v.routes.reduce((earliest, r) => {
      return !earliest || new Date(r.deadline) < new Date(earliest) ? r.deadline : earliest;
    }, "");
    
    if (!earliestDeadline) return true; // assume active if no deadline
    return new Date(earliestDeadline) >= today;
  });

  // Apply search and remote-policy filters
  const filteredVacancies = visibleVacancies.filter((v) => {
    const matchesSearch =
      v.title.toLowerCase().includes(search.toLowerCase()) ||
      v.client.toLowerCase().includes(search.toLowerCase()) ||
      v.location.toLowerCase().includes(search.toLowerCase()) ||
      v.requirements.hard.some((req) => req.toLowerCase().includes(search.toLowerCase()));

    const matchesRemote =
      remoteFilter === "all" || v.remote_policy === remoteFilter;

    return matchesSearch && matchesRemote;
  });

  if (loading) {
    return (
      <div className="flex h-[calc(100vh-4rem)] items-center justify-center bg-canvas">
        <div className="flex flex-col items-center gap-3">
          <span className="h-8 w-8 animate-spin rounded-full border-3 border-slate-300 border-t-slate-800" />
          <p className="text-sm font-medium text-slate-500">{t("common.loading")}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
      
      {/* Title / Description */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between border-b border-slate-200/60 pb-5 gap-4">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold text-slate-900 tracking-tight">
              {t("nav.vacancies")}
            </h1>
            <button
              onClick={() => navigate("/vacancies/new")}
              className="inline-flex items-center gap-1 rounded-lg bg-slate-900 hover:bg-slate-800 text-white px-3 py-1.5 text-xs font-semibold shadow-2xs hover:shadow-xs transition-all duration-150 cursor-pointer focus:outline-none"
            >
              <span>+</span>
              <span>{t("vacancy.add")}</span>
            </button>
          </div>
          <p className="mt-1.5 text-xs text-slate-400">
            {showExpired
              ? (language === "nl" ? "Verlopen vacatures worden getoond" : "Expired vacancies are shown")
              : t("vacancy.expired_hidden")}
          </p>
        </div>

        {/* Filter Controls */}
        <div className="flex flex-wrap items-center gap-4">
          {/* Search Input */}
          <div className="relative">
            <span className="absolute inset-y-0 left-3 flex items-center pointer-events-none text-slate-400 text-xs">
              🔍
            </span>
            <input
              type="text"
              placeholder="Search title, client, skills…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-64 rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 py-1.5 pl-9 pr-4 text-xs text-slate-700 dark:text-slate-355 placeholder-slate-400 shadow-2xs focus:border-slate-500 focus:outline-none transition-colors"
            />
          </div>

          {/* Remote Policy Filter */}
          <select
            value={remoteFilter}
            onChange={(e) => setRemoteFilter(e.target.value)}
            className="rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 px-3 py-1.5 text-xs text-slate-655 dark:text-slate-300 shadow-2xs focus:border-slate-500 focus:outline-none transition-colors cursor-pointer"
          >
            <option value="all">All policies</option>
            <option value="remote">Remote</option>
            <option value="hybrid">Hybrid</option>
            <option value="onsite">On-Site</option>
          </select>

          {/* Show Expired Checkbox */}
          <label className="flex items-center gap-2 cursor-pointer text-xs font-semibold text-slate-600 dark:text-slate-400">
            <input
              type="checkbox"
              checked={showExpired}
              onChange={(e) => setShowExpired(e.target.checked)}
              className="h-4 w-4 rounded-sm border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-indigo-650 focus:ring-indigo-500 cursor-pointer"
            />
            {t("vacancy.show_expired")}
          </label>
        </div>
      </div>

      {/* Vacancy cards grid */}
      {filteredVacancies.length === 0 ? (
        <div className="mt-12 rounded-2xl border border-slate-200/50 bg-white p-16 text-center shadow-xs">
          <p className="text-slate-400 text-sm">No vacancies match your search criteria.</p>
        </div>
      ) : (
        <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {filteredVacancies.map((v) => {
            const hasWf = workflows.some((w) => w.vacancy_id === v.id);
            
            // Get rate range and earliest deadline
            const rates = v.routes.map((r) => r.rate).filter(Boolean);
            const minRate = rates.length > 0 ? Math.min(...rates) : null;
            const maxRate = rates.length > 0 ? Math.max(...rates) : null;
            const rateStr = minRate === maxRate 
              ? `€${minRate}/h` 
              : `€${minRate} - €${maxRate}/h`;

            const earliestDeadline = v.routes.reduce((earliest, r) => {
              return !earliest || new Date(r.deadline) < new Date(earliest) ? r.deadline : earliest;
            }, "");
            
            const daysLeft = earliestDeadline ? getDaysRemaining(earliestDeadline) : null;
            const isUrgent = daysLeft !== null && daysLeft >= 0 && daysLeft <= 5;
            const isExpired = daysLeft !== null && daysLeft < 0;
            const isStarting = startingWfId === v.id;

            return (
              <article
                key={v.id}
                onClick={() => setSelectedVacancy(v)}
                className={`flex flex-col justify-between rounded-xl border bg-white dark:bg-slate-900 p-4 sm:p-5 shadow-2xs hover:shadow-xs transition-all duration-200 hover:scale-[1.012] cursor-pointer ${
                  isUrgent 
                    ? "border-rose-200 dark:border-rose-950 bg-linear-to-b from-rose-50/10 to-white" 
                    : isExpired
                    ? "border-slate-200/40 dark:border-slate-800 opacity-60 hover:opacity-85"
                    : "border-slate-200/60 dark:border-slate-850"
                }`}
                title="Klik om details en bewijslast te bekijken"
              >
                <div>
                  
                  {/* Top: Client & Urgency Badge */}
                  <div className="relative flex items-center justify-between gap-2 min-w-0">
                    <span 
                      className={`text-[11px] font-bold uppercase tracking-wider text-slate-400 truncate flex-1 ${isUrgent || isExpired ? "pr-14" : ""}`} 
                      title={v.client}
                    >
                      {v.client}
                    </span>
                    
                    {isUrgent && (
                      <span className="absolute right-0 top-1/2 -translate-y-1/2 inline-flex items-center gap-1 rounded-full bg-rose-50 border border-rose-100 px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider text-rose-600 shrink-0 animate-pulse-slow">
                        ⚠️ {daysLeft}d
                      </span>
                    )}

                    {isExpired && (
                      <span className="absolute right-0 top-1/2 -translate-y-1/2 inline-flex items-center gap-1 rounded-full bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider text-slate-500 shrink-0">
                        Expired
                      </span>
                    )}
                  </div>

                  {/* Vacancy Title */}
                  <h3 className="mt-2 text-sm font-bold text-slate-800 line-clamp-2">
                    {v.title}
                  </h3>

                  {/* Core Details (Location, Hours, Rate, Routes) */}
                  <div className="mt-3 flex flex-col gap-1.5 text-[11px] text-slate-500">
                    <div className="flex items-center gap-1.5 min-w-0">
                      <span className="text-slate-400 shrink-0">📍</span>
                      <span className="text-slate-600/80 truncate">{v.location}</span>
                    </div>
                    <div className="flex items-center gap-4">
                      <span className="flex items-center gap-1.5">
                        <span className="text-slate-400">🕒</span>
                        <span className="text-slate-600/80">{v.hours_per_week}h</span>
                      </span>
                      {minRate && (
                        <span className="flex items-center gap-1.5">
                          <span className="text-slate-400">💰</span>
                          <span className="text-slate-600/80">{rateStr}</span>
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-1.5 min-w-0">
                      <span className="text-slate-400 shrink-0">🔗</span>
                      <span className="text-indigo-500/95 font-medium truncate">
                        {v.routes.length} {v.routes.length === 1 ? "route" : "routes"} ({v.routes.map(r => r.portal).join(", ")})
                      </span>
                    </div>
                  </div>

                </div>

                {/* Bottom: Deadline & Start Workflow */}
                <div className="mt-4 border-t border-slate-50 pt-3 flex items-center justify-between">
                  
                  {/* Deadline info */}
                  <div className="flex flex-col">
                    <span className="text-[9px] font-bold uppercase tracking-wider text-slate-400">
                      {t("vacancy.deadline")}
                    </span>
                    <span className={`text-[11px] font-semibold mt-0.5 ${isUrgent ? "text-rose-600" : "text-slate-600"}`}>
                      {earliestDeadline
                        ? new Date(earliestDeadline).toLocaleDateString(undefined, {
                            day: "numeric",
                            month: "short",
                            year: "numeric",
                          })
                        : "N/A"}
                    </span>
                  </div>

                  {/* Action Button */}
                  {hasWf ? (
                    <button
                      disabled
                      onClick={(e) => e.stopPropagation()}
                      className="rounded-lg bg-slate-50 border border-slate-200/40 px-3 py-1.5 text-[11px] font-semibold text-slate-400 cursor-not-allowed"
                    >
                      In Workflow
                    </button>
                  ) : (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleStartWorkflow(v.id);
                      }}
                      disabled={isStarting}
                      className="rounded-lg bg-slate-900 hover:bg-slate-800 text-white px-3 py-1.5 text-[11px] font-semibold shadow-2xs hover:shadow-xs transition-all duration-150 cursor-pointer focus:outline-none disabled:bg-slate-400 disabled:cursor-wait"
                    >
                      {isStarting ? (
                        <span className="flex items-center gap-1">
                          <span className="h-2.5 w-2.5 animate-spin rounded-full border-2 border-slate-300 border-t-white" />
                          …
                        </span>
                      ) : (
                        t("workflow.start")
                      )}
                    </button>
                  )}

                </div>

              </article>
            );
          })}
        </div>
      )}

      {/* Vacancy Detail Overlay */}
      {selectedVacancy && (
        <VacancyDetailModal
          vacancy={selectedVacancy}
          repoPath={repoPath || ""}
          hasWorkflow={workflows.some((w) => w.vacancy_id === selectedVacancy.id)}
          onStartWorkflow={handleStartWorkflow}
          onClose={(deleted) => {
            setSelectedVacancy(null);
            if (deleted) {
              setReloadNonce((n) => n + 1);
            }
          }}
        />
      )}

    </div>
  );
}
